import type { LucideIcon } from "lucide-react";
import {
  Bell,
  Bookmark,
  ClipboardList,
  Coins,
  Compass,
  Crown,
  Gavel,
  Gem,
  Gift,
  HandCoins,
  Images,
  Layers,
  LayoutGrid,
  LifeBuoy,
  MessagesSquare,
  Newspaper,
  ScrollText,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Ticket,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";

import type { Dictionary } from "@/dictionaries/types";
import type { Locale } from "@/i18n-config";
import { NAV_CATALOGUE, localizeNavItem, type LocalizedNavItem } from "@/lib/navigation";

/**
 * THE v2 PRESENTATION LAYER OVER THE NAVIGATION CATALOGUE.
 *
 * The rule this file exists to enforce: `src/lib/navigation` owns WHAT a
 * surface lists and WHO may see it — the entries, their URLs, their grouping,
 * their audience (derived from the middleware's protected-route list). It does
 * NOT own how any of it looks. Icons are a design decision, and the catalogue's
 * icons belong to the legacy chrome that shipped before this design system
 * existed.
 *
 * Taking them verbatim is the mistake this file undoes. The home page's six
 * doors already fixed a vocabulary — Market is a grid, Collections is a gem,
 * Artists are people, Auctions is a gavel, Galleries are frames, the Journal is
 * a paper — and the rail was quietly contradicting it, calling collections a
 * `Grid2x2` while the tile six inches below called them a gem.
 */

/**
 * id → v2 icon. Ids not listed keep the catalogue's icon, which is how a new
 * catalogue entry appears in the chrome with a sane default rather than
 * nothing at all.
 *
 * The six that match the home page's doors are marked; the rest are chosen to
 * sit in the same family — geometric, single-weight, no filled shapes.
 */
const V2_ICONS: Partial<Record<string, LucideIcon>> = {
  home: Compass,

  market: LayoutGrid, // door
  collection: Gem, // door
  specialCollections: Crown,
  artists: Users, // door
  drops: Sparkles,
  galleries: Images, // door
  activeBid: Gavel, // door
  blogs: Newspaper, // door
  currencies: Coins,
  search: Search,

  message: MessagesSquare,
  wallet: Wallet,
  // Not a briefcase: a portfolio here is a stack of things you hold, not a job.
  portfolio: Layers,
  saved: Bookmark,
  myAuctions: ClipboardList,
  myBids: HandCoins,
  notifications: Bell,
  referrals: UserPlus,
  redeem: Gift,
  // Sliders, not a cog: the settings screen is a set of values you move.
  settings: SlidersHorizontal,

  ticket: Ticket,
  help: LifeBuoy,
  terms: ScrollText,
  privacy: ShieldCheck,
};

/** Apply the v2 icon set to a surface's items. */
export function withV2Icons(items: readonly LocalizedNavItem[]): LocalizedNavItem[] {
  return items.map((item) => {
    const icon = V2_ICONS[item.id];
    return icon ? { ...item, icon } : { ...item };
  });
}

/**
 * The Home entry, resolved outside the surface selectors.
 *
 * Needed because `home` declares `supersededWhenAuthenticated: { by:
 * "dashboard" }` on the desktop surface — so removing Dashboard below would
 * otherwise leave a signed-in viewer with no home entry anywhere in the rail.
 */
function homeEntry(lang: Locale, dict: Dictionary): LocalizedNavItem {
  const item = NAV_CATALOGUE.find((i) => i.id === "home")!;
  return localizeNavItem({ ...item, audience: "public", gated: false }, lang, dict);
}

/**
 * v2 CARRIES NO "DASHBOARD" ENTRY.
 *
 * `/` and `/dashboard` render the identical page — that is deliberate and
 * documented (the split is a caching and indexing split, not two screens) — so
 * listing both in one navigation is two names for one destination, and
 * whichever one you are not on reads as inactive.
 *
 * Home wins the slot because it is the URL a guest and a signed-in viewer
 * share, so the chrome does not rename a destination underneath someone the
 * moment they log in. `/dashboard` stays reachable from the account menu,
 * which is where a personalised URL belongs.
 */
export function v2Surface(
  items: readonly LocalizedNavItem[],
  lang: Locale,
  dict: Dictionary,
  { ensureHome = true }: { ensureHome?: boolean } = {}
): LocalizedNavItem[] {
  const out = withV2Icons(items.filter((i) => i.id !== "dashboard"));
  if (ensureHome && !out.some((i) => i.id === "home")) {
    out.unshift(withV2Icons([homeEntry(lang, dict)])[0]);
  }
  return out;
}
